/**
 * Full-screen pricing explorer — compare partner options per product category.
 */
import React from 'react';
import { createPortal } from 'react-dom';
import { Check, CurrencyDollar, Sparkle, WarningCircle, X } from '@phosphor-icons/react';
import type { PreContractProduct } from '../lib/preContractTypes';
import {
    formatDesignPartnerLine,
    optionToDesignRecommendation,
    partnerKeysEqual,
    financeMethodsEqual,
    type PreContractDesignPartnerRecommendation,
    type PreContractProductPricingOption,
} from '../lib/preContractProductPartners';

function formatMoney(value: number | null | undefined, digits = 0): string {
    if (value == null || !Number.isFinite(value)) return '—';
    return value.toLocaleString(undefined, {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: digits,
        minimumFractionDigits: digits,
    });
}

function formatPpw(value: number | null | undefined): string {
    if (value == null || !Number.isFinite(value)) return '—';
    return `$${value.toFixed(2)}/W`;
}

function displayProductName(product: PreContractProduct): string {
    if (product === 'RRR') return 'R&R';
    if (product === 'RR') return 'Remove & reinstall';
    return product;
}

export type PreContractProductPricingModalProps = {
    open: boolean;
    product: PreContractProduct;
    accent: string;
    icon: React.ReactNode;
    options: PreContractProductPricingOption[];
    selectedOption?: PreContractProductPricingOption | null;
    designPartner?: PreContractDesignPartnerRecommendation | null;
    savedPartner?: string | null;
    savedMethod?: string | null;
    onClose: () => void;
    onSelect: (option: PreContractProductPricingOption) => void;
    onSync?: () => void;
    syncDisabled?: boolean;
    syncLoading?: boolean;
    syncLabel?: string;
};

export const PreContractProductPricingModal: React.FC<PreContractProductPricingModalProps> = ({
    open,
    product,
    accent,
    icon,
    options,
    selectedOption,
    designPartner,
    savedPartner,
    savedMethod,
    onClose,
    onSelect,
    onSync,
    syncDisabled = false,
    syncLoading = false,
    syncLabel = 'Sync partner',
}) => {
    if (!open) return null;

    const productName = displayProductName(product);
    const availableCount = options.filter((option) => option.available).length;
    const savedLine =
        savedPartner?.trim()
            ? savedMethod?.trim()
                ? `${savedPartner} · ${savedMethod}`
                : savedPartner
            : 'Not saved to opportunity';

    return createPortal(
        <>
            <button
                type="button"
                className="pci-pricing-modal__backdrop"
                aria-label="Close pricing options"
                onClick={onClose}
            />
            <div
                className={`pci-pricing-modal pci-pricing-modal--${accent}`}
                role="dialog"
                aria-modal="true"
                aria-labelledby="pci-pricing-modal-title"
            >
                <header className="pci-pricing-modal__head">
                    <div className="pci-pricing-modal__title-row">
                        <span className="pci-pricing-modal__icon" aria-hidden>
                            {icon}
                        </span>
                        <div>
                            <h2 id="pci-pricing-modal-title" className="pci-pricing-modal__title">
                                {productName} pricing
                            </h2>
                            <p className="pci-pricing-modal__subtitle">
                                {availableCount > 0
                                    ? `${availableCount} finance option${availableCount === 1 ? '' : 's'} from design pricing`
                                    : 'Pricing could not be loaded for this product'}
                            </p>
                        </div>
                    </div>
                    <button
                        type="button"
                        className="pci-pricing-modal__close"
                        aria-label="Close"
                        onClick={onClose}
                    >
                        <X size={18} weight="bold" />
                    </button>
                </header>

                <div className="pci-pricing-modal__context">
                    <div className="pci-pricing-modal__context-item">
                        <span className="pci-pricing-modal__context-label">Selected</span>
                        <strong>
                            {designPartner?.available
                                ? formatDesignPartnerLine(designPartner)
                                : 'Not set'}
                        </strong>
                    </div>
                    <div className="pci-pricing-modal__context-item">
                        <span className="pci-pricing-modal__context-label">Saved on opportunity</span>
                        <strong>{savedLine}</strong>
                    </div>
                </div>

                <div className="pci-pricing-modal__body">
                    {options.length === 0 ? (
                        <div className="pci-pricing-modal__empty">
                            <WarningCircle size={28} weight="fill" aria-hidden />
                            <p>
                                {designPartner?.reason ??
                                    'Complete design and utility inputs to load pricing options.'}
                            </p>
                        </div>
                    ) : (
                        <ul className="pci-pricing-modal__options">
                            {options.map((option) => {
                                const selected =
                                    selectedOption &&
                                    partnerKeysEqual(option.partner, selectedOption.partner) &&
                                    financeMethodsEqual(option.financeMethod, selectedOption.financeMethod);
                                const line = formatDesignPartnerLine(
                                    optionToDesignRecommendation(product, option),
                                );

                                return (
                                    <li key={`${option.partner}::${option.financeMethod}`}>
                                        <button
                                            type="button"
                                            className={`pci-pricing-modal__option${
                                                selected ? ' pci-pricing-modal__option--selected' : ''
                                            }${option.recommended ? ' pci-pricing-modal__option--best' : ''}${
                                                !option.available ? ' pci-pricing-modal__option--disabled' : ''
                                            }`}
                                            disabled={!option.available}
                                            aria-pressed={Boolean(selected)}
                                            onClick={() => {
                                                if (!option.available) return;
                                                onSelect(option);
                                            }}
                                        >
                                            <div className="pci-pricing-modal__option-top">
                                                <span className="pci-pricing-modal__option-partner">
                                                    {line}
                                                </span>
                                                <div className="pci-pricing-modal__option-badges">
                                                    {option.recommended ? (
                                                        <span className="pci-pricing-modal__badge pci-pricing-modal__badge--best">
                                                            <Sparkle size={11} weight="fill" aria-hidden />
                                                            Best rate
                                                        </span>
                                                    ) : null}
                                                    {selected ? (
                                                        <span className="pci-pricing-modal__badge pci-pricing-modal__badge--selected">
                                                            <Check size={11} weight="bold" aria-hidden />
                                                            Selected
                                                        </span>
                                                    ) : null}
                                                </div>
                                            </div>
                                            <div className="pci-pricing-modal__option-metrics">
                                                <div>
                                                    <span>Monthly</span>
                                                    <strong>
                                                        {option.monthly != null && option.monthly > 0
                                                            ? `${formatMoney(option.monthly, 0)}/mo`
                                                            : '—'}
                                                    </strong>
                                                </div>
                                                {product === 'Solar' && option.ppw != null ? (
                                                    <div>
                                                        <span>PPW</span>
                                                        <strong>{formatPpw(option.ppw)}</strong>
                                                    </div>
                                                ) : null}
                                                {product === 'Solar' && option.epc != null ? (
                                                    <div>
                                                        <span>EPC</span>
                                                        <strong>{formatMoney(option.epc, 0)}</strong>
                                                    </div>
                                                ) : null}
                                            </div>
                                            {!option.available && option.reason ? (
                                                <p className="pci-pricing-modal__option-warn">
                                                    {option.reason}
                                                </p>
                                            ) : null}
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>

                <footer className="pci-pricing-modal__foot">
                    <button type="button" className="pci-pricing-modal__btn pci-pricing-modal__btn--ghost" onClick={onClose}>
                        Done
                    </button>
                    {onSync ? (
                        <button
                            type="button"
                            className="pci-pricing-modal__btn pci-pricing-modal__btn--primary"
                            disabled={syncDisabled || syncLoading || !designPartner?.available}
                            onClick={onSync}
                        >
                            <CurrencyDollar size={16} weight="duotone" aria-hidden />
                            {syncLoading ? 'Saving…' : syncLabel}
                        </button>
                    ) : null}
                </footer>
            </div>
        </>,
        document.body,
    );
};

export default PreContractProductPricingModal;
